<script setup lang="ts">
import { ref, computed } from 'vue';
import { useTranslation } from '@/utils/locales/messages';
import { useLanguageStore } from '@/shared/stores/languageStore';
import { apiClient } from '@/shared/api/client';
import { useDialog } from '@/shared/composables/useDialog';
import OfficerAppointmentForm from '@/features/startTask/components/OfficerAppointmentForm.vue';

const { warning: showWarning } = useDialog();

/** REQUEST_PAYLOAD / eFilingDataForApi 루트 필드 정규화 */
function strUen(v: unknown): string {
  return v == null || v === '' ? '' : String(v).replace(/\s/g, '').trim();
}

const languageStore = useLanguageStore();
const { createSyncT } = useTranslation();
const t = createSyncT(languageStore.currentLanguage);

interface Props {
  taskData?: any;
  taskId?: string;
  showBackButton?: boolean;
  readOnly?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  taskData: null,
  taskId: '',
  showBackButton: true,
  readOnly: false,
});

const emit = defineEmits<{
  back: [];
  submit: [data: any];
  error: [error: any];
  'validation-success': [data: { eFilingData: any; entityUEN: string; filingAgentUEN: string; entityName: string }];
}>();

const formRef = ref<InstanceType<typeof OfficerAppointmentForm> | null>(null);
const isSubmitting = ref(false);
const isValidating = ref(false);

const debugData = ref<{
  originalEFilingData: any;
  mergedEFilingData: any;
  mergedEFilingDataForApi: any;
  mergedContactData?: any;
  mergedFormData?: any;
  changes: Array<{ path: string; oldValue: any; newValue: any }>;
}>({
  originalEFilingData: null,
  mergedEFilingData: null,
  mergedEFilingDataForApi: null,
  mergedContactData: null,
  mergedFormData: null,
  changes: [],
});

const validationResult = ref<any>(null);

const formatFieldLabel = (field: any): string => {
  let label = field.label || field.field || '';
  if (label.endsWith(' Code') || label.endsWith(' Name')) {
    label = label.replace(/ Code$/, '').replace(/ Name$/, '');
  }
  return label;
};

const fieldValidationErrors = computed(() => {
  const fields = validationResult.value?.data?.missingFields || [];
  if (!fields.length) return {};

  const errors: Record<string, string> = {};
  fields.forEach((field: any) => {
    const fieldPath = field.field || '';
    const label = formatFieldLabel(field);
    const mappedField = mapFieldPathToComponentField(fieldPath);
    if (mappedField) {
      errors[mappedField] = t('startTask.This field is required') || `${label} is required`;
    }
  });

  return errors;
});

const mapFieldPathToComponentField = (fieldPath: string): string | null => {
  const fieldMap: Record<string, string> = {
    positionHeld: 'position',
    entryDate: 'appointedDate',
    birthDate: 'dateOfBirth',
    postalCode: 'postalCode',
    foreignAddr1: 'foreignAddress1',
    mobileNoCountryCode: 'mobileCountryCode',
    mobileNo: 'mobileNumber',
    'entityType.code': 'entityType',
    'entityType.name': 'entityType',
    entityName: 'entityName',
    filingAgentUEN: 'filingAgentUEN',
    entityUEN: 'entityUEN',
    name: 'contactName',
    email: 'officerEmail',
    registrationIdNo: 'registrationIdNo',
    'idType.code': 'idType',
    'idType.name': 'idType',
    'nationality.code': 'nationality',
    'nationality.name': 'nationality',
    addressLine1: 'addressLine1',
    addressLine2: 'addressLine2',
    city: 'city',
    'country.code': 'country',
    'country.name': 'country',
    jurisdiction: 'jurisdiction',
  };

  if (fieldMap[fieldPath]) return fieldMap[fieldPath];

  const pathParts = fieldPath.split('.');
  if (pathParts.length > 1) {
    const baseField = pathParts[0];
    if (fieldMap[baseField]) return fieldMap[baseField];
  }

  return null;
};

const finalEFilingData = ref<any>(null);

const entityUEN = computed(() => {
  const fromFinalData = finalEFilingData.value?.EntityUEN;
  const fromEFilingDataForApi = props.taskData?.eFilingDataForApi?.EntityUEN;
  const fromEFilingDataValidation = props.taskData?.eFilingDataValidation?.EntityUEN;
  const fromTaskData = props.taskData?.entityUEN;
  const fromAppointList = finalEFilingData.value?.AppointList?.[0]?.EntityUEN ||
    props.taskData?.eFilingDataForApi?.AppointList?.[0]?.EntityUEN ||
    props.taskData?.eFilingDataValidation?.AppointList?.[0]?.EntityUEN;
  return fromFinalData || fromEFilingDataForApi || fromEFilingDataValidation || fromTaskData || fromAppointList || '';
});

/** FilingAgentUEN 은 REQUEST_PAYLOAD 루트 값만 — EntityUEN 과 혼동·폴백 금지 */
const filingAgentUEN = computed(() => {
  const fromFinalData = finalEFilingData.value?.FilingAgentUEN;
  const fromEFilingDataForApi = props.taskData?.eFilingDataForApi?.FilingAgentUEN;
  const fromEFilingDataValidation = props.taskData?.eFilingDataValidation?.FilingAgentUEN;
  const fromTaskData = props.taskData?.filingAgentUEN;
  return fromFinalData || fromEFilingDataForApi || fromEFilingDataValidation || fromTaskData || '';
});

const entityName = computed(() => {
  return props.taskData?.eFilingData?.entityName?.name ||
    props.taskData?.eFilingData?.entityName ||
    props.taskData?.eFilingDataValidation?.entityName?.name ||
    props.taskData?.eFilingDataValidation?.entityName ||
    props.taskData?.eFilingDataForApi?.entityName ||
    props.taskData?.entityName ||
    finalEFilingData.value?.entityName ||
    '';
});

const handleBack = () => {
  emit('back');
};

const deepMerge = (base: any, update: any): any => {
  if (!base) return update;
  if (!update) return base;

  if (typeof base !== 'object' || typeof update !== 'object') {
    if (update === '' || update === null || update === undefined) {
      return base;
    }
    return update;
  }

  if (Array.isArray(base) && Array.isArray(update)) {
    const maxLen = Math.max(base.length, update.length);
    const result = [];
    for (let i = 0; i < maxLen; i++) {
      if (i < update.length && i < base.length) result.push(deepMerge(base[i], update[i]));
      else if (i < base.length) result.push(base[i]);
      else result.push(update[i]);
    }
    return result;
  }

  const result = { ...base };
  for (const key of Object.keys(update)) {
    const updateValue = update[key];
    const baseValue = base[key];

    if (typeof updateValue === 'object' && updateValue !== null && 'code' in updateValue && 'name' in updateValue) {
      if (!updateValue.code && !updateValue.name) result[key] = baseValue || updateValue;
      else if (!updateValue.code && baseValue?.code) result[key] = { ...updateValue, code: baseValue.code };
      else if (!updateValue.name && baseValue?.name) result[key] = { ...updateValue, name: baseValue.name };
      else result[key] = deepMerge(baseValue, updateValue);
    } else {
      result[key] = deepMerge(baseValue, updateValue);
    }
  }

  return result;
};

const mergeTaskDataWithEFiling = (
  baseTaskData: any,
  newEFilingData: { eFilingDataValidation: any; eFilingDataForApi: any },
  newContactData?: any,
): any => {
  const merged = JSON.parse(JSON.stringify(baseTaskData || {}));

  if (!merged.formData) {
    merged.formData = {};
  }

  if (newContactData) {
    merged.formData.contactData = newContactData;
  }

  const removeCorporateContactFields = (data: any) => {
    if (!data || !data.AppointList || !Array.isArray(data.AppointList)) {
      return data;
    }

    data.AppointList.forEach((appointItem: any) => {
      if (!appointItem.addPositionHolder) return;
      const officerCategory = appointItem.addPositionHolder.officerCategory;
      const categoryCode = typeof officerCategory === 'object' && officerCategory !== null
        ? officerCategory.code
        : officerCategory;

      if (categoryCode === 'B09001') {
        delete appointItem.addPositionHolder.contactAddressType;
        delete appointItem.addPositionHolder.contactBlkhseNo;
        delete appointItem.addPositionHolder.contactStreetName;
        delete appointItem.addPositionHolder.contactBuildingName;
        delete appointItem.addPositionHolder.contactLevelNo;
        delete appointItem.addPositionHolder.contactUnitNo;
        delete appointItem.addPositionHolder.contactPostalCode;
        delete appointItem.addPositionHolder.contactForeignAddr1;
        delete appointItem.addPositionHolder.contactForeignAddr2;
        delete appointItem.addPositionHolder.email;
        delete appointItem.addPositionHolder.mobileNoCountryCode;
        delete appointItem.addPositionHolder.mobileNoAreaCode;
        delete appointItem.addPositionHolder.mobileNo;
      }
    });

    return data;
  };

  if (merged.eFilingDataValidation) {
    merged.eFilingDataValidation = removeCorporateContactFields(merged.eFilingDataValidation);
  }
  if (merged.eFilingDataForApi) {
    merged.eFilingDataForApi = removeCorporateContactFields(merged.eFilingDataForApi);
  }

  if (merged.eFilingDataValidation && newEFilingData.eFilingDataValidation) {
    merged.eFilingDataValidation = deepMerge(merged.eFilingDataValidation, newEFilingData.eFilingDataValidation);
  } else {
    merged.eFilingDataValidation = newEFilingData.eFilingDataValidation || merged.eFilingDataValidation;
  }

  if (merged.eFilingDataForApi && newEFilingData.eFilingDataForApi) {
    merged.eFilingDataForApi = deepMerge(merged.eFilingDataForApi, newEFilingData.eFilingDataForApi);
  } else {
    merged.eFilingDataForApi = newEFilingData.eFilingDataForApi || merged.eFilingDataForApi;
  }

  if (merged.eFilingDataValidation) {
    merged.eFilingDataValidation = removeCorporateContactFields(merged.eFilingDataValidation);
  }
  if (merged.eFilingDataForApi) {
    merged.eFilingDataForApi = removeCorporateContactFields(merged.eFilingDataForApi);
  }

  return merged;
};

const handleSubmit = async () => {
  if (isSubmitting.value) return;

  try {
    isSubmitting.value = true;

    if (!formRef.value) {
      console.error('❌ [AppointDirectorEFiling] Form ref가 없습니다');
      return;
    }

    const isValid = formRef.value.validateAll?.();
    if (!isValid) {
      await showWarning(
        t('startTask.Validation Error') || 'Validation Error',
        t('startTask.Please check the required fields and enter the values') ||
          'Please check the required fields and enter the values.',
      );
      return;
    }

    const isFormValid = formRef.value.validateAll?.();
    if (!isFormValid) {
      await showWarning(
        t('startTask.Validation Error') || 'Validation Error',
        t('startTask.Please check the required fields and enter the values') ||
          'Please check the required fields and enter the values.',
      );
      return;
    }

    const updatedContactData = formRef.value.getContactData?.();
    const existingEFilingData = {
      eFilingDataValidation: props.taskData?.eFilingDataValidation,
      eFilingDataForApi: props.taskData?.eFilingDataForApi,
    };

    const updatedEFilingData = await formRef.value.getEFilingData?.(updatedContactData, existingEFilingData);
    const mergedData = mergeTaskDataWithEFiling(props.taskData, updatedEFilingData, updatedContactData);

    debugData.value = {
      originalEFilingData: props.taskData?.eFilingDataValidation || null,
      mergedEFilingData: mergedData.eFilingDataValidation || null,
      mergedEFilingDataForApi: mergedData.eFilingDataForApi || null,
      mergedContactData: mergedData.formData?.contactData || null,
      mergedFormData: mergedData.formData,
      changes: [],
    };

    await handleValidateAndSubmit();
  } finally {
    isSubmitting.value = false;
  }
};

const handleValidateAndSubmit = async () => {
  if (isValidating.value) return;

  try {
    isValidating.value = true;

    if (!props.taskId) {
      validationResult.value = {
        success: false,
        error: 'Task ID is required',
      };
      return;
    }

    const savePayload: any = {
      eFilingDataValidation: debugData.value.mergedEFilingData,
      eFilingDataForApi: debugData.value.mergedEFilingDataForApi,
    };

    if (debugData.value.mergedContactData) {
      savePayload.contactData = debugData.value.mergedContactData;
    }

    await apiClient.put(`/startTask/officer-appointment/update-efiling-data/${props.taskId}`, savePayload);
    const response = await apiClient.get(`/startTask/officer-appointment/validate/${props.taskId}`);

    validationResult.value = {
      success: true,
      data: response?.data || response,
    };

    if (validationResult.value?.data?.isValid) {
      finalEFilingData.value = debugData.value.mergedEFilingDataForApi;
      const api = finalEFilingData.value || {};
      const entityUForSingpass =
        strUen(api.EntityUEN ?? api.entityUEN) ||
        strUen(api.AppointList?.[0]?.EntityUEN) ||
        strUen(props.taskData?.eFilingDataForApi?.EntityUEN) ||
        strUen(props.taskData?.eFilingDataValidation?.EntityUEN) ||
        strUen(props.taskData?.entityUEN) ||
        '';
      const filingAgentForSingpass =
        strUen(api.FilingAgentUEN ?? api.filingAgentUEN) ||
        strUen(props.taskData?.eFilingDataForApi?.FilingAgentUEN) ||
        strUen(props.taskData?.eFilingDataValidation?.FilingAgentUEN) ||
        strUen(props.taskData?.filingAgentUEN) ||
        '';
      const en = api.entityName;
      const nameForSingpass =
        (typeof en === 'string' && en.trim() ? en.trim() : '') ||
        (en && typeof en === 'object' ? String(en.name ?? en.value ?? '').trim() : '') ||
        entityName.value;
      emit('validation-success', {
        eFilingData: api,
        entityUEN: entityUForSingpass,
        filingAgentUEN: filingAgentForSingpass,
        entityName: nameForSingpass,
      });
    } else {
      await showWarning(
        t('startTask.Validation Error') || 'Validation Error',
        t('startTask.Please check the required fields and enter the values') ||
          'Please check the required fields and enter the values.',
      );
    }
  } catch (error: any) {
    console.error('❌ [AppointDirectorEFiling] Save & Validate 실패:', error);
    validationResult.value = {
      success: false,
      error: error.message || 'Save or Validation failed',
    };
  } finally {
    isValidating.value = false;
  }
};

defineExpose({
  formRef,
  getEFilingData: async () => {
    if (formRef.value) {
      return await formRef.value.getEFilingData?.();
    }
    return null;
  },
  getMergedData: async () => {
    if (formRef.value) {
      const updatedEFilingData = await formRef.value.getEFilingData?.();
      return mergeTaskDataWithEFiling(props.taskData, updatedEFilingData);
    }
    return null;
  },
});
</script>

<template>
  <v-card rounded="md" class="officer-appointment-efiling">
    <v-card-text>
      <OfficerAppointmentForm
        ref="formRef"
        :initial-data="taskData"
        :read-only="readOnly"
        :show-task-flow="false"
        :e-filing-mode="!!props.taskId"
        :validation-errors="fieldValidationErrors"
      />
    </v-card-text>
    <v-divider v-if="!readOnly" />
    <v-card-actions v-if="!readOnly" class="justify-end pa-4 pr-8">
      <v-btn
        v-if="showBackButton"
        variant="outlined"
        color="primary"
        :disabled="isSubmitting || isValidating"
        @click="handleBack"
      >
        {{ t('startTask.Back') || 'Back' }}
      </v-btn>
      <v-btn
        color="primary"
        variant="flat"
        :loading="isSubmitting || isValidating"
        :disabled="isSubmitting || isValidating"
        @click="handleSubmit"
      >
        {{ t('startTask.Submit') || 'Submit' }}
      </v-btn>
    </v-card-actions>

    <v-divider v-if="readOnly" />
    <v-card-actions v-if="readOnly" class="justify-center pa-4">
      <v-alert type="success" variant="tonal" density="compact" class="mb-0">
        {{ t('startTask.E-Filing has been completed') || 'E-Filing has been completed' }}
      </v-alert>
    </v-card-actions>
  </v-card>
</template>

<style scoped>
.officer-appointment-efiling {
  margin: 16px;
  max-width: 100%;
  box-sizing: border-box;
}

.officer-appointment-efiling :deep(.v-card-text) {
  max-width: 100%;
  box-sizing: border-box;
}

.officer-appointment-efiling :deep(.v-row) {
  margin-left: 0;
  margin-right: 0;
}

.officer-appointment-efiling :deep(.v-col) {
  padding-left: 8px;
  padding-right: 8px;
}

.officer-appointment-efiling :deep(.v-card-actions) {
  padding-right: 32px !important;
}
</style>
